import React, { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AssignmentTurnedIn,
  CheckCircleOutline,
  EventNote,
  MenuBook,
  MoreVert,
  Save,
  SwapHoriz,
  TrendingUp,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { attendanceAPI, teacherModuleAPI } from '../../services/api';
import { batchesAPI } from '../../services/batches';
import PunctualityStatusChip from '../Reports/PunctualityStatusChip';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import ClassLogForm from './ClassLogForm';
import PreviousClassPlanSection from './PreviousClassPlanSection';
import StudentReadOnlyDialog from '../StudentReadOnlyDialog';
import StudentHandoverButton from '../StudentHandoverButton';
import ClassObservationCard from './ClassObservationCard';
import {
  isClassLogComplete,
  classLogAlreadySaved,
  hasNextPlan,
  parseSessionLog,
  validateClassLogBeforeSave,
  validateNextPlanBeforeSave,
  normalizeClassLogJson,
  normalizeNextPlanJson,
  getLegacyClassLogPreviewText,
  getLegacyNextPlanPreviewText,
  LMS_ASSIGNMENT_REQUIRED_MESSAGE,
  emptyClassLogJsonV2,
  emptyNextPlanJsonV2,
  emptyClassLogJson as emptyClassLogJsonUtil,
  emptyNextPlanJson as emptyNextPlanJsonUtil,
  buildClassLogDraftFromPreviousPlan,
} from '../../utils/classSessionLog';
import {
  isAllStudentsAbsentFromStudents,
  isSessionWorkWaived,
  isNextPlanRequiredForSessionDate,
  isNextPlanMissing,
} from '../../utils/sessionWorkRequirements';
import { formatCalendarDate, formatTimeOnly, getTodayYMDInTimezone, isSessionDateInFuture } from '../../utils/dateTimeUtils';
import { formatSessionClockLines } from '../../utils/sessionClockDisplay';
import { isMakeupSession } from '../../utils/sessionKindDisplay';
import { invalidateTeacherCalendar } from '../../utils/invalidateTeacherCalendar';
import { formatCycleRange } from '../../utils/courseCycleProgress';
import {
  CourseCycleColumnHeader,
  CourseCycleProgressFraction,
} from './CourseCycleProgress';
import SessionCoverDialog from './SessionCoverDialog';
import CancelClassDialog from './CancelClassDialog';
import {
  mergeBatchRosterForSessionDisplay,
  unwrapBatchStudentsList,
} from '../../utils/batchDetail';

function isEmptyText(v) {
  return !String(v || '').trim();
}

function isIanaTimezone(tz) {
  return typeof tz === 'string' && tz.includes('/');
}

function getIanaOffsetMinutes(timeZone, utcDate) {
  const tz = (timeZone && String(timeZone).trim()) ? String(timeZone).trim() : 'UTC';
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(utcDate);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const y = Number(get('year'));
  const m = Number(get('month'));
  const d = Number(get('day'));
  const hh = Number(get('hour'));
  const mm = Number(get('minute'));
  const ss = Number(get('second'));
  const asUTC = Date.UTC(y, m - 1, d, hh, mm, ss);
  return (asUTC - utcDate.getTime()) / 60000;
}

function zonedDateTimeToUtcMs(dateStrYYYYMMDD, timeStrHHMMSS, timeZone) {
  if (!dateStrYYYYMMDD || !timeStrHHMMSS) return null;
  const tz = (timeZone && String(timeZone).trim()) ? String(timeZone).trim() : 'UTC';
  const [y, m, d] = String(dateStrYYYYMMDD).slice(0, 10).split('-').map(Number);
  const t = String(timeStrHHMMSS).trim().slice(0, 8);
  const normalized = t.length === 5 ? `${t}:00` : t;
  const [hh, mm, ss] = normalized.split(':').map(Number);
  if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null;

  const guessUtcMs = Date.UTC(y, m - 1, d, hh, mm, ss || 0);
  const guessDate = new Date(guessUtcMs);

  const offsetsHours = {
    UTC: 0,
    IST: 5.5,
    EST: -5,
    PST: -8,
    CST: -6,
    MST: -7,
    GMT: 0,
    AEST: 10,
    AEST_QLD: 10,
    AWST: 8,
    ACST: 9.5,
    AST: -4,
    NST: -3.5,
    HST: -10,
    AKST: -9,
  };

  const offsetMinutes = isIanaTimezone(tz)
    ? getIanaOffsetMinutes(tz, guessDate)
    : (offsetsHours[tz] != null ? offsetsHours[tz] * 60 : 0);

  return guessUtcMs - offsetMinutes * 60000;
}

export default function TeacherSessionPanel({ sessionId, autoOpenAction = null, batchStudents: batchStudentsProp = null }) {
  const queryClient = useQueryClient();
  const { user, isTeacher, isTeachingLead, isTeacherSupervisor, isTeacherAdmin, isAdmin, isCountryManager } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openSessionCover, setOpenSessionCover] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const canManageSessionCover =
    isAdmin() || isTeachingLead() || isCountryManager();

  const detailQuery = useQuery(['teacherSessionDetail', sessionId], async () => {
    const res = await teacherModuleAPI.getSessionDetail(sessionId);
    return res.data?.data;
  });

  const approvalQuery = useQuery(['teacherSessionApproval', sessionId], async () => {
    const res = await teacherModuleAPI.getSessionApproval(sessionId);
    return res.data?.data;
  });

  const startClassMutation = useMutation(
    (asRole) => teacherModuleAPI.startSession(sessionId, asRole ? { as: asRole } : {}),
    {
      onSuccess: (res, asRole) => {
        queryClient.invalidateQueries(['teacherSessionDetail', sessionId]);
        const already = res.data?.data?.alreadyStarted;
        const who = asRole === 'secondary' ? 'Secondary class start' : 'Class start';
        toast.success(already ? `${who} was already recorded` : `${who} recorded`);
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Could not record class start'),
    }
  );

  const zoomConfirmMutation = useMutation(
    ({ participantId, studentId }) =>
      teacherModuleAPI.confirmZoomParticipant(sessionId, participantId, { student_id: studentId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['teacherSessionDetail', sessionId]);
        toast.success('Participant mapped');
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Could not map participant'),
    }
  );

  const cancelClassMutation = useMutation(
    (payload) => teacherModuleAPI.cancelSession(sessionId, payload),
    {
      onSuccess: (_data, payload) => {
        setOpenCancelDialog(false);
        queryClient.invalidateQueries(['teacherSessionDetail', sessionId]);
        queryClient.invalidateQueries(['teacherPendingItems']);
        queryClient.invalidateQueries(['teacherHome']);
        invalidateTeacherCalendar(queryClient);
        toast.success(
          payload?.cancelReason === 'holiday'
            ? 'Marked as holiday — paid class, no makeup expected'
            : 'Class cancelled — pending items cleared'
        );
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Could not cancel class'),
    }
  );

  const uncancelClassMutation = useMutation(
    () => teacherModuleAPI.uncancelSession(sessionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['teacherSessionDetail', sessionId]);
        queryClient.invalidateQueries(['teacherPendingItems']);
        queryClient.invalidateQueries(['teacherHome']);
        invalidateTeacherCalendar(queryClient);
        toast.success('Class restored — you can continue with attendance and class work');
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Could not undo cancel'),
    }
  );

  const handleUndoCancel = () => {
    if (uncancelClassMutation.isLoading) return;
    const ok = window.confirm(
      'Restore this class? It will no longer be cancelled, and attendance / homework / class log will be required again.'
    );
    if (ok) uncancelClassMutation.mutate();
  };

  const templatesQuery = useQuery(['teacherHomeworkTemplates'], async () => {
    const res = await teacherModuleAPI.getHomeworkTemplates();
    return res.data?.data || [];
  });

  const data = detailQuery.data;
  const session = data?.session;
  const mergeInfo = data?.mergeInfo || null;
  const incomingMerge = data?.incomingMerge || null;
  const classStart = data?.classStart;
  const secondaryClassStart = data?.secondaryClassStart;
  const zoomMeetingUrl = data?.zoomMeetingUrl || session?.zoom_meeting_url || null;
  const zoom = data?.zoom || null;
  const students = useMemo(() => data?.students || [], [data?.students]);
  const sessionLog = data?.sessionLog || null;
  const previousClassPlan = data?.previousClassPlan || null;

  const batchRosterQuery = useQuery(
    ['batchStudents', session?.batch_id],
    () => batchesAPI.getStudents(session.batch_id),
    {
      enabled: !!session?.batch_id && batchStudentsProp == null,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    }
  );

  const batchRosterStudents = useMemo(() => {
    if (batchStudentsProp != null) return batchStudentsProp;
    return unwrapBatchStudentsList(batchRosterQuery.data);
  }, [batchStudentsProp, batchRosterQuery.data]);

  const rosterStudents = useMemo(
    () => mergeBatchRosterForSessionDisplay(batchRosterStudents, students),
    [batchRosterStudents, students]
  );

  const rosterHasIneligible = rosterStudents.some((s) => !s.sessionEligible);

  const optionsQuery = useQuery(
    ['teacherClassLogOptions', sessionId, session?.teacher_id],
    async () => {
      const params = !isTeacher() && session?.teacher_id ? { teacherId: session.teacher_id } : undefined;
      const res = await teacherModuleAPI.getClassLogOptions(params);
      return res.data?.data || null;
    },
    { enabled: !!session }
  );

  const [classLogJson, setClassLogJson] = useState(emptyClassLogJsonUtil());
  const [lastPlanSuggestion, setLastPlanSuggestion] = useState(null);
  const classLogDraftGuardRef = useRef({ sessionId: null, touched: false, suggestionOn: false });
  const [nextPlanJson, setNextPlanJson] = useState(emptyNextPlanJsonUtil());
  const [showClassLogValidation, setShowClassLogValidation] = useState(false);
  const [showNextPlanValidation, setShowNextPlanValidation] = useState(false);
  const [homeworkTemplateId, setHomeworkTemplateId] = useState('');
  const [homeworkMessage, setHomeworkMessage] = useState('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [completionComment, setCompletionComment] = useState('');

  const [openAttendance, setOpenAttendance] = useState(false);
  const [openHomework, setOpenHomework] = useState(false);
  const [openCompletion, setOpenCompletion] = useState(false);
  const [openNotes, setOpenNotes] = useState(false);
  const [openApprovalRequest, setOpenApprovalRequest] = useState(false);
  const [approvalRequestReason, setApprovalRequestReason] = useState('');
  const [attendanceDraft, setAttendanceDraft] = useState({});
  const [studentDetailId, setStudentDetailId] = useState(null);
  const [studentDetailOpen, setStudentDetailOpen] = useState(false);

  const openStudentDetails = (studentId) => {
    setStudentDetailId(studentId);
    setStudentDetailOpen(true);
  };

  const hasLmsModules = (optionsQuery.data?.modules || []).length > 0;

  const legacyClassLogPreview = useMemo(() => {
    if (!sessionLog) return null;
    const raw = sessionLog.classLogJson || sessionLog.class_log_json;
    return getLegacyClassLogPreviewText(raw, sessionLog.batch_notes);
  }, [sessionLog]);

  const legacyNextPlanPreview = useMemo(() => {
    if (!sessionLog) return null;
    const raw = sessionLog.nextPlanJson || sessionLog.next_plan_json;
    return getLegacyNextPlanPreviewText(raw, sessionLog.weekly_plan);
  }, [sessionLog]);

  React.useEffect(() => {
    const hasLms = (optionsQuery.data?.modules || []).length > 0;
    const guard = classLogDraftGuardRef.current;
    if (guard.sessionId !== sessionId) {
      guard.sessionId = sessionId;
      guard.touched = false;
      guard.suggestionOn = false;
      setLastPlanSuggestion(null);
    }

    let serverClassLog;
    let serverNextPlan;
    if (sessionLog) {
      const parsed = parseSessionLog(sessionLog);
      const isClassV2 = Number(parsed.classLogJson?.version) === 2;
      const isPlanV2 = Number(parsed.nextPlanJson?.version) === 2;
      serverClassLog = hasLms ? (isClassV2 ? parsed.classLogJson : emptyClassLogJsonV2()) : parsed.classLogJson;
      serverNextPlan = hasLms ? (isPlanV2 ? parsed.nextPlanJson : emptyNextPlanJsonV2()) : parsed.nextPlanJson;
      setHomeworkTemplateId(sessionLog.homework_template_id || '');
      setHomeworkMessage(sessionLog.homework_message || '');
      setIsCompleted(!!sessionLog.is_completed);
      setCompletionComment(sessionLog.completion_comment || '');
    } else {
      serverClassLog = hasLms ? emptyClassLogJsonV2() : emptyClassLogJsonUtil();
      serverNextPlan = hasLms ? emptyNextPlanJsonV2() : emptyNextPlanJsonUtil();
      setHomeworkTemplateId('');
      setHomeworkMessage('');
      setIsCompleted(false);
      setCompletionComment('');
    }
    setNextPlanJson(serverNextPlan);

    const savedClassLog = classLogAlreadySaved(sessionLog);
    if (savedClassLog) {
      guard.touched = false;
      guard.suggestionOn = false;
      setLastPlanSuggestion(null);
      setClassLogJson(serverClassLog);
      return;
    }
    if (guard.touched || guard.suggestionOn) return;
    setClassLogJson(serverClassLog);
    setLastPlanSuggestion(null);
  }, [sessionLog, optionsQuery.data, sessionId]);

  const onClassLogDraftChange = (next) => {
    setClassLogJson(next);
    setShowClassLogValidation(false);
    const guard = classLogDraftGuardRef.current;
    guard.touched = true;
    if (guard.suggestionOn) {
      setLastPlanSuggestion((prev) => (prev && !prev.edited ? { ...prev, edited: true } : prev));
    }
  };

  React.useEffect(() => {
    // Initialize draft attendance from API payload
    const init = {};
    (students || []).forEach((s) => {
      init[s.student_id] = s.attendanceStatus || '';
    });
    setAttendanceDraft(init);
  }, [students]);

  const approvalStatus = approvalQuery.data?.status || 'none';

  const permissions = data?.permissions;
  const isCancelled = !!permissions?.isCancelled;
  const isHoliday = !!permissions?.isHoliday || session?.session_kind === 'holiday';
  const classClosed = isCancelled || isHoliday;
  const isMakeupClass = isMakeupSession(session);
  const isMerged = !!permissions?.isMerged || session?.session_kind === 'merged';
  const canCancelSession = !!permissions?.canCancel;
  const canUndoCancel = !!permissions?.canUndoCancel;
  const canWriteSession = permissions?.canWriteSession ?? false;
  const showStartPrimary = permissions?.showStartPrimary ?? canWriteSession;
  const showStartSecondary = permissions?.showStartSecondary ?? false;

  const allStudentsAbsent = useMemo(
    () => isAllStudentsAbsentFromStudents(students, attendanceDraft),
    [students, attendanceDraft]
  );

  const sessionWorkWaived = useMemo(
    () => isSessionWorkWaived({ allStudentsAbsent, isCancelled: classClosed || isMerged }),
    [allStudentsAbsent, classClosed, isMerged]
  );

  const needsApproval = useMemo(() => {
    if (classClosed || isMerged || allStudentsAbsent) return false;
    if (permissions?.beforeBatchStart) return false;
    if (!session?.session_date) return false;
    const tz = session?.teacher_timezone || user?.timezone || 'UTC';
    const endUtcMs = zonedDateTimeToUtcMs(session.session_date, session?.end_time || '', tz);
    if (endUtcMs) {
      const cutoffMs = endUtcMs + 48 * 60 * 60 * 1000;
      if (Date.now() <= cutoffMs) return false;
    }
    const today = getTodayYMDInTimezone(tz);
    const isPastOrToday = session.session_date <= today;
    if (!isPastOrToday) return false;
    const attendanceMissing = students.length ? students.some((s) => !s.attendanceStatus) : false;
    const notesMissing = !isClassLogComplete(
      sessionLog?.classLogJson || sessionLog?.class_log_json,
      sessionLog?.batch_notes
    );
    const hwMissing =
      isEmptyText(sessionLog?.homework_message) &&
      !sessionLog?.homework_template_id &&
      isEmptyText(sessionLog?.homework_pdf_url) &&
      !(data?.homeworkImages || []).length;
    const nextPlanMissing = isNextPlanMissing({
      sessionDate: session.session_date,
      hasPlan: hasNextPlan(
        sessionLog?.nextPlanJson || sessionLog?.next_plan_json,
        sessionLog?.weekly_plan
      ),
      waived: false,
    });
    const logMissing = notesMissing || hwMissing || !sessionLog?.is_completed;
    return attendanceMissing || logMissing || nextPlanMissing;
  }, [allStudentsAbsent, classClosed, data?.homeworkImages, isMerged, permissions?.beforeBatchStart, session?.end_time, session?.session_date, session?.teacher_timezone, sessionLog, students, user?.timezone]);

  const canEdit = useMemo(() => {
    if (!canWriteSession) return false;
    if (!isTeacher()) return true;
    if (!needsApproval) return true;
    if (isAdmin()) return true;
    return approvalStatus === 'approved';
  }, [approvalStatus, isAdmin, isTeacher, needsApproval, canWriteSession]);

  React.useEffect(() => {
    const sessionTz = session?.teacher_timezone || user?.timezone;
    const future =
      !session ||
      !!permissions?.beforeBatchStart ||
      isSessionDateInFuture(session.session_date, sessionTz);
    if (!canEdit || future || classClosed) return;
    const guard = classLogDraftGuardRef.current;
    if (guard.sessionId !== sessionId || guard.touched || guard.suggestionOn) return;
    if (classLogAlreadySaved(sessionLog)) return;
    const modules = optionsQuery.data?.modules || [];
    if (!modules.length) return;
    const draft = buildClassLogDraftFromPreviousPlan(previousClassPlan, modules);
    if (!draft) return;
    guard.suggestionOn = true;
    setClassLogJson(draft);
    const dateLabel = previousClassPlan?.fromSessionDate
      ? formatCalendarDate(previousClassPlan.fromSessionDate)
      : '';
    setLastPlanSuggestion({
      edited: false,
      fromDateLabel: dateLabel && dateLabel !== '—' ? dateLabel : '',
    });
  }, [
    canEdit,
    classClosed,
    permissions?.beforeBatchStart,
    session,
    user?.timezone,
    sessionId,
    sessionLog,
    optionsQuery.data,
    previousClassPlan,
  ]);

  const supervisorViewOnly =
    (isTeacherSupervisor() || isCountryManager()) && !isAdmin() && !isTeacherAdmin() && !canWriteSession;

  /** Current class only (do not use batch-wide pending list — other sessions would keep rows stuck on Pending). */
  const sessionDeadlineAttendancePending = useMemo(() => {
    if (!students.length || sessionWorkWaived) return false;
    return students.some((s) => {
      const st = attendanceDraft[s.student_id] ?? s.attendanceStatus;
      return !st || !String(st).trim();
    });
  }, [students, attendanceDraft, sessionWorkWaived]);

  const sessionDeadlineHomeworkPending = useMemo(
    () => !sessionWorkWaived && !(sessionLog?.homework_sent_at || sessionLog?.homeworkSentAt),
    [sessionLog?.homework_sent_at, sessionLog?.homeworkSentAt, sessionWorkWaived]
  );

  const sessionDeadlineClassLogPending = useMemo(
    () => !sessionWorkWaived && !isClassLogComplete(classLogJson),
    [classLogJson, sessionWorkWaived]
  );
  const serverClassLogSaved = useMemo(() => classLogAlreadySaved(sessionLog), [sessionLog]);
  const classLogSuggestion = serverClassLogSaved ? null : lastPlanSuggestion;
  const classLogAwaitingSave = !!classLogSuggestion;
  const classLogNeedsSave = sessionWorkWaived ? false : classLogAwaitingSave || sessionDeadlineClassLogPending;

  const nextPlanRequired = useMemo(
    () =>
      !sessionWorkWaived && isNextPlanRequiredForSessionDate(session?.session_date),
    [sessionWorkWaived, session?.session_date]
  );

  const sessionDeadlineNextPlanPending = useMemo(
    () =>
      isNextPlanMissing({
        sessionDate: session?.session_date,
        hasPlan: hasNextPlan(nextPlanJson),
        waived: sessionWorkWaived,
      }),
    [nextPlanJson, session?.session_date, sessionWorkWaived]
  );

  const sessionDeadlineNextPlanSuggested = useMemo(
    () => !sessionWorkWaived && !nextPlanRequired && !hasNextPlan(nextPlanJson),
    [nextPlanJson, nextPlanRequired, sessionWorkWaived]
  );

  React.useEffect(() => {
    const action = autoOpenAction || new URLSearchParams(location.search || '').get('action');
    if (!action) return;
    if (action === 'attendance') setOpenAttendance(true);
    if (action === 'homework') setOpenHomework(true);
    if (action === 'notes') setOpenNotes(true);
    if (action === 'completion') setOpenCompletion(true);
    if (action === 'cancel') setOpenCancelDialog(true);
  }, [autoOpenAction, location.search]);

  React.useEffect(() => {
    const action = autoOpenAction || new URLSearchParams(location.search || '').get('action');
    if (action !== 'nextPlan' || !session) return;
    const el = document.getElementById('next-class-plan');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [autoOpenAction, location.search, session]);

  const attendanceMutation = useMutation((payload) => attendanceAPI.upsertRecord(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(['teacherSessionDetail', sessionId]);
      if (session?.batch_id) queryClient.invalidateQueries(['batchPendingItems', session.batch_id]);
      queryClient.invalidateQueries(['teacherMyPendingItems']);
      toast.success('Attendance updated');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update attendance'),
  });

  const saveLogMutation = useMutation(
    ({ toastMessage, ...body }) => teacherModuleAPI.upsertSessionLog(sessionId, body),
    {
    onSuccess: (res, variables) => {
      const row = res?.data?.data;
      if (row && typeof row === 'object') {
        queryClient.setQueryData(['teacherSessionDetail', sessionId], (old) => {
          if (!old) return old;
          return {
            ...old,
            sessionLog: { ...(old.sessionLog || {}), ...row },
          };
        });
      }
      queryClient.invalidateQueries(['teacherSessionDetail', sessionId]);
      if (session?.batch_id) queryClient.invalidateQueries(['batchPendingItems', session.batch_id]);
      queryClient.invalidateQueries(['teacherMyPendingItems']);
      if (variables?.closeCompletion) setOpenCompletion(false);
      if (variables?.closeNotes) setOpenNotes(false);
      toast.success(variables?.toastMessage || 'Saved');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save'),
  }
  );

  const sessionMetaPayload = () => ({
    homeworkTemplateId: homeworkTemplateId || null,
    homeworkMessage,
    isCompleted,
    completedAt: isCompleted ? new Date().toISOString() : null,
    completionComment: completionComment || null,
    homeworkPdfUrl: null,
  });

  const saveClassLog = (options = {}) => {
    if (!hasLmsModules) {
      toast.error(LMS_ASSIGNMENT_REQUIRED_MESSAGE);
      return;
    }
    const validation = validateClassLogBeforeSave(classLogJson);
    if (!validation.ok) {
      setShowClassLogValidation(true);
      toast.error(validation.message);
      return;
    }
    saveLogMutation.mutate({
      ...sessionMetaPayload(),
      classLogJson: normalizeClassLogJson(classLogJson),
      toastMessage: 'Class log saved',
      closeNotes: options.closeNotes,
    });
  };

  const saveNextPlan = (options = {}) => {
    if (!hasLmsModules) {
      toast.error(LMS_ASSIGNMENT_REQUIRED_MESSAGE);
      return;
    }
    const validation = validateNextPlanBeforeSave(nextPlanJson);
    if (!validation.ok) {
      setShowNextPlanValidation(true);
      toast.error(validation.message);
      return;
    }
    if (nextPlanRequired && !hasNextPlan(nextPlanJson)) {
      setShowNextPlanValidation(true);
      toast.error('Add at least one module or a note for the next class.');
      return;
    }
    saveLogMutation.mutate({
      ...sessionMetaPayload(),
      nextPlanJson: normalizeNextPlanJson(nextPlanJson),
      toastMessage: 'Next class plan saved',
      closeNotes: options.closeNotes,
    });
  };

  const saveSessionMeta = (options = {}) => {
    const hasHomework = !!(homeworkTemplateId || String(homeworkMessage || '').trim());
    const hasCompletion = isCompleted || !!String(completionComment || '').trim();
    if (!hasHomework && !hasCompletion) {
      toast.error('Nothing to save — add homework or update completion first.');
      return;
    }
    saveLogMutation.mutate({
      ...sessionMetaPayload(),
      toastMessage: options.toastMessage || 'Saved',
      closeCompletion: options.closeCompletion,
      closeNotes: options.closeNotes,
    });
  };

  const requestApproval = async () => {
    try {
      await teacherModuleAPI.requestSessionApproval(sessionId, {
        requestReason: approvalRequestReason.trim() || null,
      });
      toast.success('Approval requested');
      setOpenApprovalRequest(false);
      setApprovalRequestReason('');
      approvalQuery.refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to request approval');
    }
  };

  const isSavingClassLog = saveLogMutation.isLoading && !!saveLogMutation.variables?.classLogJson;
  const isSavingNextPlan = saveLogMutation.isLoading && !!saveLogMutation.variables?.nextPlanJson;

  if (detailQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!session) {
    return <Alert severity="error">Session not found.</Alert>;
  }

  const sessionTz = session?.teacher_timezone || user?.timezone;
  const clockLines = formatSessionClockLines({
    sessionDate: session.session_date,
    startTime: session.start_time,
    endTime: session.end_time,
    teacherTimezone: sessionTz,
    students: data?.assignedStudentZones?.length ? data.assignedStudentZones : rosterStudents,
  });
  const todayYMD = getTodayYMDInTimezone(sessionTz);
  const sessionMarkedCompleted = !!(sessionLog?.is_completed);
  const isTodaySession = session.session_date === todayYMD;
  const isBeforeBatchStart = !!permissions?.beforeBatchStart;
  const isFutureSession =
    isBeforeBatchStart || isSessionDateInFuture(session.session_date, sessionTz);
  const canMutateSession = canEdit && !isFutureSession && !classClosed;
  const canClickPrimaryStart = showStartPrimary && !isFutureSession && !classClosed && !isMerged;
  const canClickSecondaryStart = showStartSecondary && !isFutureSession && !classClosed && !isMerged;
  const bothStartButtons = showStartPrimary && showStartSecondary;
  const substituteName = String(session.substitute_teacher_name || '').trim();
  const substituteStarted =
    !!substituteName &&
    !!classStart?.teacherJoinedAt &&
    !!session.substitute_user_id &&
    !!session.teacher_joined_by &&
    String(session.substitute_user_id) === String(session.teacher_joined_by);
  const primaryStartLabel = classStart?.teacherJoinedAt
    ? substituteStarted
      ? `${substituteName} started ${classStart.teacherJoinedDisplay || ''}`.trim()
      : `${bothStartButtons ? 'Primary started' : 'Started'} ${classStart.teacherJoinedDisplay || ''}`.trim()
    : bothStartButtons
      ? 'Start class (primary)'
      : 'Start class';

  const handleOpenCancelDialog = () => setOpenCancelDialog(true);
  const roomLabel = session?.room_name || session?.room || null; // optional, if backend provides in future

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3,
        borderColor: 'rgba(0,0,0,0.08)',
        bgcolor: '#fff',
      }}
    >
      <Stack spacing={1.25} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.15 }}>
              {session.batch_name || 'Batch'}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  {clockLines.teacherLine}
                </Typography>
                {roomLabel ? (
                  <Typography variant="caption" color="text.secondary">
                    • Room {roomLabel}
                  </Typography>
                ) : null}
              </Stack>
              {clockLines.studentLine ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {clockLines.studentLine}
                </Typography>
              ) : null}
            </Box>
            <Stack spacing={0.25} sx={{ mt: 1 }}>
              <Typography variant="body1" fontWeight={800} sx={{ color: '#0b3954' }}>
                Primary: {session.primary_teacher_name || 'Not assigned'}
              </Typography>
              <Typography variant="body1" fontWeight={800} sx={{ color: '#0b3954' }}>
                Secondary: {session.secondary_teacher_name || 'Not assigned'}
              </Typography>
              {substituteName ? (
                <Typography variant="body1" fontWeight={800} sx={{ color: '#0b3954' }}>
                  Substitute: {substituteName}
                </Typography>
              ) : null}
            </Stack>
          </Box>

          {isHoliday ? (
            <Chip
              label={isMakeupClass ? 'HOLIDAY MAKEUP' : 'HOLIDAY'}
              size="small"
              sx={{ bgcolor: 'rgba(245, 158, 11, 0.16)', color: '#b45309', fontWeight: 800 }}
            />
          ) : isMakeupClass ? (
            <Chip
              label={isCancelled ? 'CANCELLED MAKEUP' : 'MAKEUP'}
              size="small"
              sx={{
                bgcolor: 'rgba(75,42,167,0.12)',
                color: '#4b2aa7',
                fontWeight: 800,
              }}
            />
          ) : isCancelled ? (
            <Chip
              label="CANCELLED"
              size="small"
              sx={{
                bgcolor: 'rgba(158,158,158,0.15)',
                color: '#616161',
                fontWeight: 800,
              }}
            />
          ) : isMerged || incomingMerge?.sources?.length ? (
            <Chip
              label="MERGED"
              size="small"
              sx={{
                bgcolor: 'rgba(123,31,162,0.12)',
                color: '#6a1b9a',
                fontWeight: 800,
              }}
            />
          ) : sessionMarkedCompleted ? (
            <Chip
              label="COMPLETED"
              size="small"
              sx={{
                bgcolor: 'rgba(46,125,50,0.12)',
                color: '#2e7d32',
                fontWeight: 800,
              }}
            />
          ) : isTodaySession ? (
            <Chip
              label="ACTIVE SESSION"
              size="small"
              sx={{
                bgcolor: 'rgba(25,118,210,0.08)',
                color: '#1976d2',
                fontWeight: 800,
              }}
            />
          ) : null}
        </Stack>

        {(isAdmin() || isTeachingLead()) && session?.id ? (
          <ClassObservationCard sessionId={session.id} />
        ) : null}

        {isMerged ? (
          <Alert severity="info">
            This class is merged into{' '}
            <strong>{mergeInfo?.targetBatchName || 'another batch'}</strong>
            {mergeInfo?.targetSessionDateLabel || mergeInfo?.targetSessionDate
              ? ` on ${mergeInfo.targetSessionDateLabel || mergeInfo.targetSessionDate}`
              : ''}
            {mergeInfo?.targetStartTime
              ? ` · ${formatTimeOnly(mergeInfo.targetStartTime)}${
                  mergeInfo.targetEndTime ? ` – ${formatTimeOnly(mergeInfo.targetEndTime)}` : ''
                }`
              : ''}
            {mergeInfo?.targetTeacherName ? ` · Host: ${mergeInfo.targetTeacherName}` : ''}. Students should join
            that class (use the host teacher’s Zoom).
            {mergeInfo?.targetBatchId ? (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (mergeInfo.targetSessionId) params.set('sessionId', mergeInfo.targetSessionId);
                    if (mergeInfo.targetSessionDate) params.set('sessionDate', mergeInfo.targetSessionDate);
                    const qs = params.toString();
                    navigate(`/batches/${mergeInfo.targetBatchId}${qs ? `?${qs}` : ''}`);
                  }}
                >
                  Open host batch
                </Button>
              </Box>
            ) : null}
          </Alert>
        ) : incomingMerge?.sources?.length ? (
          <Alert severity="info">
            <strong>{incomingMerge.sourceBatchNames || incomingMerge.sources.map((s) => s.sourceBatchName).join(', ')}</strong>
            {' '}merged into this class. Those students join this Zoom. Their names are in the list
            below — mark their attendance on this class.
          </Alert>
        ) : null}

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          {showStartPrimary ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => startClassMutation.mutate('primary')}
              disabled={!canClickPrimaryStart || startClassMutation.isLoading}
            >
              {primaryStartLabel}
            </Button>
          ) : null}
          {showStartSecondary ? (
            <Button
              variant={showStartPrimary ? 'outlined' : 'contained'}
              size="small"
              onClick={() => startClassMutation.mutate('secondary')}
              disabled={!canClickSecondaryStart || startClassMutation.isLoading}
            >
              {secondaryClassStart?.teacherJoinedAt
                ? `Secondary started ${secondaryClassStart.teacherJoinedDisplay || ''}`
                : 'Start class (secondary)'}
            </Button>
          ) : null}
          {zoomMeetingUrl && !isMerged ? (
            <Button
              variant="outlined"
              size="small"
              component="a"
              href={zoomMeetingUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Zoom
            </Button>
          ) : null}
          {classStart?.teacherJoinedAt && showStartPrimary ? (
            <PunctualityStatusChip status={classStart.punctualityStatus} />
          ) : null}
          {secondaryClassStart?.teacherJoinedAt && showStartSecondary ? (
            <PunctualityStatusChip status={secondaryClassStart.punctualityStatus} />
          ) : null}
          {canCancelSession ? (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleOpenCancelDialog}
              disabled={cancelClassMutation.isLoading}
            >
              Cancel class
            </Button>
          ) : null}
          {canUndoCancel ? (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleUndoCancel}
              disabled={uncancelClassMutation.isLoading}
            >
              Undo cancel
            </Button>
          ) : null}
        </Stack>

        <CancelClassDialog
          open={openCancelDialog}
          onClose={() => setOpenCancelDialog(false)}
          onConfirm={(payload) => cancelClassMutation.mutate(payload)}
          loading={cancelClassMutation.isLoading}
        />

        {zoom?.available ? (
          <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Zoom presence
            </Typography>
            {zoom.actualStart ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {zoom.actualEnd
                  ? `Ran ${new Date(zoom.actualStart).toLocaleTimeString()} – ${new Date(zoom.actualEnd).toLocaleTimeString()}`
                  : `Started ${new Date(zoom.actualStart).toLocaleTimeString()}`}
                {zoom.durationSeconds ? ` (${Math.round(zoom.durationSeconds / 60)} min)` : ''}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {zoom.status === 'no_show'
                  ? 'No Zoom meeting detected for this class yet.'
                  : 'Waiting for Zoom class data…'}
              </Typography>
            )}
            {(zoom.participants || []).length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name in Zoom</TableCell>
                      <TableCell>Student</TableCell>
                      <TableCell align="right">Duration</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(zoom.participants || []).map((p) => (
                      <TableRow key={p.zoomParticipantId}>
                        <TableCell>{p.displayName}</TableCell>
                        <TableCell>
                          {!p.studentId && p.matchMethod === 'unresolved' ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <TextField
                                select
                                size="small"
                                defaultValue=""
                                sx={{ minWidth: 160 }}
                                onChange={(e) => {
                                  const sid = e.target.value;
                                  if (!sid) return;
                                  zoomConfirmMutation.mutate({
                                    participantId: p.zoomParticipantId,
                                    studentId: sid,
                                  });
                                }}
                                disabled={!canMutateSession || zoomConfirmMutation.isLoading}
                              >
                                <MenuItem value="">Map to…</MenuItem>
                                {rosterStudents
                                  .filter((s) => s.sessionEligible !== false)
                                  .map((s) => (
                                    <MenuItem key={s.student_id} value={s.student_id}>
                                      {s.student_name || s.student_id}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            </Stack>
                          ) : (
                            p.studentName || '—'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {p.participationPct != null ? `${Math.round(p.participationPct)}%` : '—'}
                        </TableCell>
                        <TableCell>{p.latenessStatus || (p.matchMethod === 'unresolved' ? 'Unresolved' : '—')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
          </Paper>
        ) : null}

        {isHoliday ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            {isMakeupClass ? 'This makeup class is a holiday.' : 'This class is a holiday.'} It counts as a paid
            class day. Attendance, homework, and class log are not required, and no makeup is expected.
            {sessionLog?.completion_comment ? ` Note: ${sessionLog.completion_comment}` : ''}
          </Alert>
        ) : isCancelled ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            {isMakeupClass ? 'This makeup class was cancelled.' : 'This class was cancelled.'} Attendance,
            homework, and class log are not required.
            {sessionLog?.completion_comment ? ` Note: ${sessionLog.completion_comment}` : ''}
          </Alert>
        ) : null}

        {!classClosed && allStudentsAbsent ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            All students were marked absent. Class log, homework, next class plan, and completion are not required.
          </Alert>
        ) : null}

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <ActionTile
            label="ATTENDANCE"
            icon={<AssignmentTurnedIn fontSize="small" />}
            onClick={() => setOpenAttendance(true)}
            disabled={!canMutateSession}
          />
          <ActionTile
            label="HOMEWORK"
            icon={<AssignmentTurnedIn fontSize="small" />}
            onClick={() => navigate(`/teacher/homework?sessionId=${encodeURIComponent(sessionId)}`)}
            disabled={!canMutateSession}
          />
          <ActionTile
            label="ADD PROGRESS"
            icon={<TrendingUp fontSize="small" />}
            primary
            onClick={() => {}}
            disabled
          />
          <ActionTile
            label="SET COMPLETION"
            icon={<CheckCircleOutline fontSize="small" />}
            onClick={() => setOpenCompletion(true)}
            disabled={!canMutateSession}
          />
          <ActionTile
            label="COVER"
            icon={<SwapHoriz fontSize="small" />}
            onClick={() => setOpenSessionCover(true)}
            disabled={!canManageSessionCover || session?.session_kind === 'merged'}
          />
        </Stack>
      </Stack>

      {needsApproval && !canEdit && !isFutureSession ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Approval required before filling attendance/homework/notes for this class.
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setOpenApprovalRequest(true)}
              disabled={approvalStatus === 'pending' || approvalStatus === 'approved'}
            >
              {approvalStatus === 'pending' ? 'Approval requested' : approvalStatus === 'approved' ? 'Approved' : 'Request approval'}
            </Button>
          </Box>
        </Alert>
      ) : null}

      {supervisorViewOnly ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only view of another teacher&apos;s class. Open your own schedule to edit attendance and homework.
        </Alert>
      ) : null}

      {isFutureSession ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {isBeforeBatchStart ? (
            <>
              This batch starts on {session.batch_start_date || 'its scheduled start date'}. Classes before that date
              are not active — attendance, homework, and notes unlock once the batch has started.
            </>
          ) : (
            <>
              This class is scheduled for a future date. Attendance, homework, batch notes, and completion unlock on the
              class date.
            </>
          )}
        </Alert>
      ) : null}

      <Box sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={900}>
            Students in Batch
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {rosterStudents.length} Students Enrolled
            {students.length !== rosterStudents.length
              ? ` (${students.length} eligible for this class)`
              : ''}
          </Typography>
        </Stack>

        {rosterHasIneligible ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Some students were assigned after this class date. They appear in the roster below; attendance
            is recorded from their first eligible class onward.
          </Alert>
        ) : null}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>ID Number</TableCell>
                <TableCell sx={{ minWidth: { xs: 100, sm: 140 } }}>
                  <CourseCycleColumnHeader />
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rosterStudents.slice(0, 6).map((s) => {
                const name = s.student_name || 'Student';
                const initials = name
                  .split(' ')
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((p) => p[0]?.toUpperCase())
                  .join('');
                const done = Number(s.classesCompleted) || 0;
                const denom =
                  Number(s.progressDenominator) > 0 ? Number(s.progressDenominator) : 20;
                const pct = Math.min(100, Math.round((done / Math.max(1, denom)) * 100));
                const winLabel = formatCycleRange(s.progressWindowStart, s.progressWindowEnd);
                return (
                  <TableRow key={s.student_id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <StudentHandoverButton
                          studentId={s.student_id}
                          batchId={session?.batch_id || null}
                        />
                        <Avatar sx={{ width: 30, height: 30, fontSize: 12 }}>{initials}</Avatar>
                        <Box>
                          <Link
                            component="button"
                            type="button"
                            variant="body2"
                            fontWeight={800}
                            underline="hover"
                            onClick={() => openStudentDetails(s.student_id)}
                            sx={{ textAlign: 'left', display: 'block' }}
                          >
                            {name}
                          </Link>
                          {!s.sessionEligible ? (
                            <Chip label="After this class" size="small" variant="outlined" sx={{ mt: 0.5 }} />
                          ) : null}
                          {s.mergedIn && s.sourceBatchName ? (
                            <Chip
                              label={s.sourceBatchName}
                              size="small"
                              sx={{ mt: 0.5, ml: 0.5, bgcolor: 'rgba(123,31,162,0.12)', color: '#6a1b9a', fontWeight: 700 }}
                            />
                          ) : null}
                          {s.plannedLeave ? (
                            <Chip label="Planned leave" size="small" color="warning" sx={{ mt: 0.5, ml: 0.5 }} />
                          ) : null}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {s.student_display_id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: { xs: 100, sm: 160 } }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: { xs: 120, sm: 200 },
                        }}
                      >
                        {winLabel ? `planned · ${winLabel}` : 'planned in cycle'}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ flex: 1, minWidth: 48 }}>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                              height: 8,
                              borderRadius: 8,
                              bgcolor: 'rgba(25,118,210,0.12)',
                              '& .MuiLinearProgress-bar': { borderRadius: 8, bgcolor: '#1976d2' },
                            }}
                          />
                        </Box>
                        <CourseCycleProgressFraction
                          classesCompleted={s.classesCompleted}
                          progressDenominator={s.progressDenominator}
                          progressWindowStart={s.progressWindowStart}
                          progressWindowEnd={s.progressWindowEnd}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" disabled>
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rosterStudents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      No students found for this batch.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>

        {rosterStudents.length > 6 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button size="small" sx={{ textTransform: 'none', fontWeight: 800 }} disabled>
              View All Students
            </Button>
          </Box>
        ) : null}
      </Box>

      <Stack spacing={2.5} sx={{ mt: 3 }}>
        <PreviousClassPlanSection previousClassPlan={previousClassPlan} />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '2px solid',
            borderColor: sessionWorkWaived || !classLogNeedsSave ? 'primary.main' : 'warning.main',
            boxShadow: '0 4px 20px rgba(25, 118, 210, 0.08)',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 2.5 },
              py: 1.75,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
              <Stack direction="row" spacing={1.25} alignItems="center">
                <MenuBook sx={{ fontSize: 28, opacity: 0.95 }} />
                <Box>
                  <Typography variant="h6" fontWeight={900} lineHeight={1.2}>
                    Class Log
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.25 }}>
                    Required · What was covered in today&apos;s class
                  </Typography>
                </Box>
              </Stack>
              <Chip
                label={
                  sessionWorkWaived
                    ? 'Not required'
                    : classLogAwaitingSave
                      ? 'Not saved yet'
                      : sessionDeadlineClassLogPending
                        ? 'Pending'
                        : 'Complete'
                }
                size="small"
                sx={{
                  fontWeight: 900,
                  bgcolor: sessionWorkWaived
                    ? 'grey.300'
                    : classLogNeedsSave
                      ? 'warning.light'
                      : 'success.light',
                  color: sessionWorkWaived
                    ? 'text.secondary'
                    : classLogNeedsSave
                      ? 'warning.dark'
                      : 'success.dark',
                }}
              />
            </Stack>
          </Box>
          <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#fff' }}>
            <ClassLogForm
              key={`class-log-panel-${sessionId}`}
              section="class"
              classLogJson={classLogJson}
              nextPlanJson={nextPlanJson}
              lmsOptions={optionsQuery.data}
              legacyClassLogPreview={legacyClassLogPreview}
              legacyNextPlanPreview={legacyNextPlanPreview}
              onClassLogChange={onClassLogDraftChange}
              onNextPlanChange={setNextPlanJson}
              disabled={!canMutateSession}
              showValidationHint={showClassLogValidation}
              lastPlanSuggestion={classLogSuggestion}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={isSavingClassLog ? <CircularProgress size={20} color="inherit" /> : <Save />}
                onClick={saveClassLog}
                disabled={!canMutateSession || !hasLmsModules || saveLogMutation.isLoading}
                sx={{
                  minWidth: { xs: '100%', sm: 220 },
                  py: 1.25,
                  fontWeight: 900,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: '0 4px 14px rgba(25, 118, 210, 0.35)',
                }}
              >
                {isSavingClassLog ? 'Saving…' : 'Save Class Log'}
              </Button>
            </Box>
          </Box>
        </Paper>

        <Paper
          id="next-class-plan"
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '2px solid',
            borderColor: 'info.main',
            boxShadow: '0 4px 20px rgba(2, 136, 209, 0.08)',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 2.5 },
              py: 1.75,
              bgcolor: 'info.main',
              color: 'info.contrastText',
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
              <Stack direction="row" spacing={1.25} alignItems="center">
                <EventNote sx={{ fontSize: 28, opacity: 0.95 }} />
                <Box>
                  <Typography variant="h6" fontWeight={900} lineHeight={1.2}>
                    Next Class Plan
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.25 }}>
                    {nextPlanRequired
                      ? 'Required · Plan modules for the upcoming session'
                      : 'Optional · Plan modules for the upcoming session'}
                  </Typography>
                </Box>
              </Stack>
              <Chip
                label={
                  sessionWorkWaived
                    ? 'Not required'
                    : sessionDeadlineNextPlanPending
                      ? 'Pending'
                      : sessionDeadlineNextPlanSuggested
                        ? 'Recommended'
                        : 'Added'
                }
                size="small"
                sx={{
                  fontWeight: 900,
                  bgcolor: sessionWorkWaived
                    ? 'rgba(255,255,255,0.2)'
                    : sessionDeadlineNextPlanPending
                      ? 'warning.light'
                      : sessionDeadlineNextPlanSuggested
                        ? 'rgba(255,255,255,0.2)'
                        : 'success.light',
                  color: sessionWorkWaived
                    ? 'info.contrastText'
                    : sessionDeadlineNextPlanPending
                      ? 'warning.dark'
                      : sessionDeadlineNextPlanSuggested
                        ? 'info.contrastText'
                        : 'success.dark',
                }}
              />
            </Stack>
          </Box>
          <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#fff' }}>
            <ClassLogForm
              key={`plan-log-panel-${sessionId}`}
              section="plan"
              classLogJson={classLogJson}
              nextPlanJson={nextPlanJson}
              lmsOptions={optionsQuery.data}
              legacyClassLogPreview={legacyClassLogPreview}
              legacyNextPlanPreview={legacyNextPlanPreview}
              onClassLogChange={setClassLogJson}
              onNextPlanChange={(next) => {
                setNextPlanJson(next);
                setShowNextPlanValidation(false);
              }}
              disabled={!canMutateSession}
              showPlanValidationHint={showNextPlanValidation}
              nextPlanRequired={nextPlanRequired}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                variant="contained"
                color="info"
                size="large"
                startIcon={isSavingNextPlan ? <CircularProgress size={20} color="inherit" /> : <Save />}
                onClick={saveNextPlan}
                disabled={!canMutateSession || !hasLmsModules || saveLogMutation.isLoading}
                sx={{
                  minWidth: { xs: '100%', sm: 240 },
                  py: 1.25,
                  fontWeight: 900,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: '0 4px 14px rgba(2, 136, 209, 0.35)',
                }}
              >
                {isSavingNextPlan ? 'Saving…' : 'Save Next Class Plan'}
              </Button>
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>
            Upcoming Deadlines
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ flexWrap: 'wrap' }}
            useFlexGap
          >
            <DeadlineRow label="Attendance" status={sessionDeadlineAttendancePending ? 'pending' : 'done'} />
            <DeadlineRow
              label="Homework"
              status={sessionWorkWaived ? 'waived' : sessionDeadlineHomeworkPending ? 'pending' : 'done'}
            />
            <DeadlineRow
              label="Class Log"
              status={sessionWorkWaived ? 'waived' : classLogNeedsSave ? 'pending' : 'done'}
            />
            <DeadlineRow
              label="Next Plan"
              status={
                sessionWorkWaived
                  ? 'waived'
                  : sessionDeadlineNextPlanPending
                    ? 'pending'
                    : sessionDeadlineNextPlanSuggested
                      ? 'recommended'
                      : 'done'
              }
              optional={!nextPlanRequired}
            />
          </Stack>
        </Paper>
      </Stack>

      {/* Exceptional approval request */}
      <Dialog
        open={openApprovalRequest}
        onClose={() => {
          setOpenApprovalRequest(false);
          setApprovalRequestReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request exceptional approval</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The normal window to fill this class has passed. Optionally explain why you need approval to
            complete attendance, homework, notes, or the next class plan.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Reason (optional)"
            value={approvalRequestReason}
            onChange={(e) => setApprovalRequestReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenApprovalRequest(false);
              setApprovalRequestReason('');
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={requestApproval}>
            Submit request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attendance dialog */}
      <Dialog open={openAttendance} onClose={() => setOpenAttendance(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" fontWeight={900}>
                Class Attendance
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Marking for: {session?.session_date}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={`TOTAL STUDENTS ${students.length}`}
                sx={{ fontWeight: 900 }}
                variant="outlined"
              />
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {isFutureSession ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Attendance can be marked on or after the class date.
            </Alert>
          ) : null}
          {!canEdit ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Approval required before editing attendance for this session.
            </Alert>
          ) : null}
          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.25, bgcolor: 'rgba(0,0,0,0.02)', display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" fontWeight={900} color="text.secondary">
                STUDENT NAME
              </Typography>
              <Typography variant="caption" fontWeight={900} color="text.secondary">
                ATTENDANCE STATUS
              </Typography>
            </Box>
            <Divider />

            <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
              {(students || []).map((s) => {
                const name = s.student_name || 'Student';
                const initials = name
                  .split(' ')
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((p) => p[0]?.toUpperCase())
                  .join('');
                const current = attendanceDraft[s.student_id] ?? '';
                const setStatus = (status) => {
                  setAttendanceDraft((prev) => ({ ...prev, [s.student_id]: status }));
                  if (!canMutateSession) return;
                  attendanceMutation.mutate({
                    session_id: s.attendanceSessionId || sessionId,
                    student_id: s.student_id,
                    status,
                  });
                };

                return (
                  <Box key={s.student_id} sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar sx={{ width: 34, height: 34, fontSize: 12 }}>{initials}</Avatar>
                      <Box>
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          fontWeight={800}
                          underline="hover"
                          onClick={() => openStudentDetails(s.student_id)}
                          sx={{ textAlign: 'left', display: 'block' }}
                        >
                          {name}
                        </Link>
                        <Typography variant="caption" color="text.secondary">
                          Roll No: {s.student_display_id || '—'}
                          {s.mergedIn && s.sourceBatchName ? ` · ${s.sourceBatchName}` : ''}
                          {s.plannedLeave ? ' · Planned leave' : ''}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        size="small"
                        variant={current === 'present' ? 'contained' : 'outlined'}
                        color="success"
                        disabled={!canMutateSession || attendanceMutation.isLoading}
                        onClick={() => setStatus('present')}
                        sx={{ minWidth: 92, textTransform: 'none', fontWeight: 900, borderRadius: 2 }}
                      >
                        Present
                      </Button>
                      <Button
                        size="small"
                        variant={current === 'absent' ? 'contained' : 'outlined'}
                        color="error"
                        disabled={!canMutateSession || attendanceMutation.isLoading}
                        onClick={() => setStatus('absent')}
                        sx={{ minWidth: 92, textTransform: 'none', fontWeight: 900, borderRadius: 2 }}
                      >
                        Absent
                      </Button>
                      <IconButton
                        size="small"
                        disabled={!canMutateSession || attendanceMutation.isLoading}
                        onClick={() => setStatus('makeup')}
                        title="Mark as makeup"
                      >
                        <SwapHoriz fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                );
              })}
              {(students || []).length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No students found.
                  </Typography>
                </Box>
              ) : null}
            </Box>

            <Divider />
            <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}>
              <Button size="small" sx={{ textTransform: 'none', fontWeight: 800 }} disabled>
                View All {students.length} Students
              </Button>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAttendance(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Homework dialog */}
      <Dialog open={openHomework} onClose={() => setOpenHomework(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Homework</DialogTitle>
        <DialogContent dividers>
          {isFutureSession ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Homework can be saved on or after the class date.
            </Alert>
          ) : null}
          {!canEdit ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Approval required before editing homework/notes for this session.
            </Alert>
          ) : null}
          <Stack spacing={1.5}>
            <TextField
              select
              label="Homework Template"
              size="small"
              value={homeworkTemplateId}
              onChange={(e) => setHomeworkTemplateId(e.target.value)}
              disabled={!canMutateSession}
            >
              <MenuItem value="">Standard Branded Template (v1)</MenuItem>
              {(templatesQuery.data || []).map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.title}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              multiline
              minRows={4}
              label="Homework"
              value={homeworkMessage}
              onChange={(e) => setHomeworkMessage(e.target.value)}
              disabled={!canMutateSession}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHomework(false)}>Close</Button>
          <Button variant="contained" onClick={() => saveSessionMeta()} disabled={!canMutateSession || saveLogMutation.isLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Completion dialog */}
      <Dialog open={openCompletion} onClose={() => setOpenCompletion(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Batch session completion</DialogTitle>
        <DialogContent dividers>
          {isFutureSession ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Completion can be set on or after the class date.
            </Alert>
          ) : null}
          <Stack spacing={2}>
            <TextField
              select
              label="Class completion"
              size="small"
              value={isCompleted ? 'yes' : 'no'}
              onChange={(e) => setIsCompleted(e.target.value === 'yes')}
              disabled={!canMutateSession}
              fullWidth
            >
              <MenuItem value="no">Not completed</MenuItem>
              <MenuItem value="yes">Completed</MenuItem>
            </TextField>
            <TextField
              multiline
              minRows={3}
              label="Comment (optional)"
              placeholder="e.g. ran over time, covered extra revision, etc."
              value={completionComment}
              onChange={(e) => setCompletionComment(e.target.value)}
              disabled={!canMutateSession}
              fullWidth
              inputProps={{ maxLength: 2000 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCompletion(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => saveSessionMeta({ closeCompletion: true, toastMessage: 'Completion saved' })}
            disabled={!canMutateSession || saveLogMutation.isLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes dialog */}
      <Dialog open={openNotes} onClose={() => setOpenNotes(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Class log & next plan</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <ClassLogForm
              key={`class-notes-dialog-${sessionId}`}
              section="class"
              classLogJson={classLogJson}
              nextPlanJson={nextPlanJson}
              lmsOptions={optionsQuery.data}
              legacyClassLogPreview={legacyClassLogPreview}
              legacyNextPlanPreview={legacyNextPlanPreview}
              onClassLogChange={onClassLogDraftChange}
              onNextPlanChange={setNextPlanJson}
              disabled={!canMutateSession}
              showValidationHint={showClassLogValidation}
              lastPlanSuggestion={classLogSuggestion}
            />
            <ClassLogForm
              key={`plan-notes-dialog-${sessionId}`}
              section="plan"
              classLogJson={classLogJson}
              nextPlanJson={nextPlanJson}
              lmsOptions={optionsQuery.data}
              legacyClassLogPreview={legacyClassLogPreview}
              legacyNextPlanPreview={legacyNextPlanPreview}
              onClassLogChange={setClassLogJson}
              onNextPlanChange={(next) => {
                setNextPlanJson(next);
                setShowNextPlanValidation(false);
              }}
              disabled={!canMutateSession}
              showPlanValidationHint={showNextPlanValidation}
              nextPlanRequired={nextPlanRequired}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setOpenNotes(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={isSavingClassLog ? <CircularProgress size={18} color="inherit" /> : <Save />}
            onClick={() => saveClassLog({ closeNotes: true })}
            disabled={!canMutateSession || !hasLmsModules || saveLogMutation.isLoading}
            sx={{ fontWeight: 900, textTransform: 'none' }}
          >
            Save Class Log
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={isSavingNextPlan ? <CircularProgress size={18} color="inherit" /> : <Save />}
            onClick={() => saveNextPlan({ closeNotes: true })}
            disabled={!canMutateSession || !hasLmsModules || saveLogMutation.isLoading}
            sx={{ fontWeight: 900, textTransform: 'none' }}
          >
            Save Next Plan
          </Button>
        </DialogActions>
      </Dialog>

      <StudentReadOnlyDialog
        open={studentDetailOpen}
        onClose={() => {
          setStudentDetailOpen(false);
          setStudentDetailId(null);
        }}
        studentId={studentDetailId}
        batchId={session?.batch_id || null}
      />

      <SessionCoverDialog
        open={openSessionCover}
        onClose={() => setOpenSessionCover(false)}
        sessionId={sessionId}
        batchName={session?.batch_name}
        currentSubstituteId={session?.substitute_teacher_id || ''}
      />
    </Paper>
  );
}

function ActionTile({ icon, label, onClick, primary = false, disabled = false }) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      variant={primary ? 'contained' : 'outlined'}
      sx={{
        width: 132,
        height: 72,
        borderRadius: 3,
        textTransform: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        fontWeight: 900,
        borderColor: 'rgba(0,0,0,0.08)',
      }}
    >
      {icon}
      <Typography variant="caption" fontWeight={900} sx={{ lineHeight: 1 }}>
        {label}
      </Typography>
    </Button>
  );
}

function DeadlineRow({ label, status, optional = false }) {
  const isDone = status === 'done';
  const isRecommended = status === 'recommended';
  const isWaived = status === 'waived';
  let chipLabel = 'Pending';
  let chipColor = 'warning';
  if (isWaived) {
    chipLabel = 'Not required';
    chipColor = 'default';
  } else if (isDone) {
    chipLabel = optional ? 'Added' : 'Done';
    chipColor = 'success';
  } else if (isRecommended) {
    chipLabel = 'Recommended';
    chipColor = 'info';
  }
  return (
    <Box
      sx={{
        flex: { sm: '1 1 140px' },
        px: 1.5,
        py: 1,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        bgcolor: 'rgba(0,0,0,0.02)',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="body2" fontWeight={700}>
          {label}
        </Typography>
        <Chip
          size="small"
          label={chipLabel}
          color={chipColor}
          variant="outlined"
          sx={{ fontWeight: 800 }}
        />
      </Stack>
    </Box>
  );
}

